import { type CoverMeta, api } from './api.ts';

/**
 * De coverfoto achter de hero zetten.
 *
 * De foto bezet alleen de hero en vervaagt naar beneden toe in de
 * weerachtergrond; onder de hero blijft de pagina één effen kleur. Waarom die
 * grens er is en niet verder: docs/adr/0001-coverfoto-naast-de-weerachtergrond.md.
 *
 * Hij hangt als eigen laag in de body en niet als pseudo-element van de hero.
 * De hero staat in een kolom van hooguit 1320 px, en een foto die daar stopt
 * leest als een ingelegd paneel in plaats van als een cover. Een laag op
 * paginaniveau is vanzelf vensterbreed — zonder `100vw`, dat op een zichtbare
 * schuifbalk stukloopt, en zonder `overflow` op een voorouder, wat de plakkende
 * tabelkoppen verderop de pagina zou breken.
 */

/** Ondergrens van de sluier: ook een perfect passende foto krijgt dit. */
export const SCRIM_MIN = 0.45;
/** Hoeveel er bij komt naarmate de foto met het thema botst. */
export const SCRIM_RANGE = 0.4;
/** Hoeveel er bij komt voor een bonte foto. */
export const SCRIM_BUSY = 0.15;
/** De spreiding waarbij die opslag vol is; daarboven wordt het niet erger. */
export const SPREAD_REFERENCE = 0.25;
/** Bovengrens; daarboven is er van de foto niets meer over. */
export const SCRIM_MAX = 0.9;

/**
 * Hoe dekkend de sluier over de foto moet liggen, 0 tot 1.
 *
 * De sluier heeft de kleur van de pagina eronder — precies de kleur waarop de
 * herotekst ontworpen is. Twee dingen bepalen hoeveel er nodig is.
 *
 * Ten eerste hoe ver de foto van die kleur af ligt: in het donkere thema is een
 * lichte foto het probleem, in het lichte thema een donkere.
 *
 * Ten tweede hoe bónt de foto is. Dat tweede stond er eerst niet in, en dat was
 * de fout: een luchtfoto meet gemiddeld 0,42 en kreeg daarmee een sluier die
 * klopte voor een effen vlak van die helderheid — terwijl de tekst in
 * werkelijkheid deels op donkere boomkruinen stond. De spreiding vangt dat.
 *
 * Wat het nog steeds niet kan: weten wáár in de foto het licht zit. Een foto met
 * een lage spreiding en één felle plek precies achter de temperatuur krijgt een
 * te zwakke sluier. Daarvoor zou je per regio moeten meten en de sluier
 * ongelijkmatig moeten maken; de halo achter de tekst is het vangnet daarvoor.
 *
 * Zuiver en apart, zodat web/tools/cover-check.mjs kan narekenen dat elke
 * inkttint over het hele bereik zijn lat haalt.
 */
export function scrimOpacity(luminance: number, spread: number, dark: boolean): number {
  const clash = clamp(dark ? luminance : 1 - luminance);
  const busy = Math.min(1, Math.max(0, spread) / SPREAD_REFERENCE);
  return Math.min(SCRIM_MAX, SCRIM_MIN + SCRIM_RANGE * clash + SCRIM_BUSY * busy);
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Zet de coverlaag klaar, of ruimt hem op.
 *
 * `hero` dient alleen als maatlat: de laag krijgt precies zijn hoogte, zodat de
 * foto ophoudt waar de hero ophoudt. Een `ResizeObserver` houdt dat bij, want
 * die hoogte verandert bij het draaien van een telefoon, bij een themawissel die
 * de curve hertekent, en bij elke verversing van de hero.
 */
export function applyCover(hero: HTMLElement, cover: CoverMeta | null, dark: boolean): void {
  if (!cover) {
    layer?.remove();
    layer = null;
    observer?.disconnect();
    observer = null;
    document.body.classList.remove('has-cover');
    return;
  }

  const node = layer ?? create();
  node.style.setProperty('--cover-image', `url("${api.coverUrl(cover)}")`);
  node.style.setProperty(
    '--cover-scrim',
    String(scrimOpacity(cover.luminance, cover.spread, dark)),
  );
  document.body.classList.add('has-cover');

  // De hero kan bij elke verversing een ander element zijn; opnieuw aankoppelen
  // is goedkoper dan bijhouden welke het nu was.
  observer?.disconnect();
  observer = new ResizeObserver(() => resize(node, hero));
  observer.observe(hero);
  resize(node, hero);
}

let layer: HTMLElement | null = null;
let observer: ResizeObserver | null = null;

function create(): HTMLElement {
  const node = document.createElement('div');
  node.className = 'cover';
  // Puur decor: geen rol, geen tekst, en niets voor een schermlezer om te melden.
  node.setAttribute('aria-hidden', 'true');
  document.body.prepend(node);
  layer = node;
  return node;
}

/**
 * De laag begint bovenaan de pagina en moet ophouden waar de hero ophoudt.
 *
 * Dus niet de hóógte van de hero, maar zijn onderkant gemeten vanaf de bovenkant
 * van het document: daar zit ook de bovenmarge van de kolom in, en die hoort bij
 * de foto — anders staat er een streep paginakleur boven het beeld.
 *
 * Via `offsetTop` en niet via `getBoundingClientRect()`, want die laatste telt
 * transformaties mee. De hero vliegt bij de eerste verf binnen met
 * `translateY(18px)` (de `rise`-animatie), en dan meet je een onderkant die
 * achttien pixels te laag ligt. Een `ResizeObserver` merkt dat niet op — die
 * kijkt naar de doosafmeting, niet naar een transformatie die uitdempt — dus de
 * laag zou te hoog blijven staan tot iets anders de hero toevallig hertekent.
 */
function documentBottom(node: HTMLElement): number {
  let y = node.offsetHeight;
  let current: HTMLElement | null = node;
  while (current) {
    y += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return y;
}

function resize(node: HTMLElement, hero: HTMLElement): void {
  node.style.setProperty('--cover-height', `${Math.round(documentBottom(hero))}px`);
}
